// Zentrale API-Anbindung. Jeder Netzwerkzugriff läuft hier durch, damit
// Token-Handling und Fehlerfälle an genau einer Stelle liegen.

import { API_URSPRUNG } from './plattform';

// Im Browser relativ wie bisher; in der nativen Hülle mit vollständiger
// Adresse, weil es dort kein „/api" am eigenen Ursprung gibt.
const BASIS = `${API_URSPRUNG}/api`;
const TOKEN_SCHLUESSEL = 'lp_token';

export type FehlerCode =
  | 'TENANT_FROZEN'        // Zugang des Unternehmens eingefroren
  | 'USER_FROZEN'          // einzelner Benutzer gesperrt
  | 'FEATURE_NOT_IN_PLAN'  // Funktion nicht im Tarif enthalten
  | 'USER_LIMIT_REACHED'   // Benutzerlimit des Tarifs erreicht
  | 'USER_GONE'
  | string;

export class ApiFehler extends Error {
  status: number;
  code?: FehlerCode;
  daten?: Record<string, unknown>;

  constructor(status: number, nachricht: string, code?: FehlerCode, daten?: Record<string, unknown>) {
    super(nachricht);
    this.name = 'ApiFehler';
    this.status = status;
    this.code = code;
    this.daten = daten;
  }

  /** Zugang gesperrt — der Benutzer muss abgemeldet werden. */
  get istGesperrt() {
    return this.code === 'TENANT_FROZEN' || this.code === 'USER_FROZEN' || this.code === 'USER_GONE';
  }

  /** Funktion nicht gebucht — Hinweis auf den Tarif statt einer Fehlermeldung. */
  get istTarifgrenze() {
    return this.status === 402;
  }
}

export function token(): string | null {
  try { return localStorage.getItem(TOKEN_SCHLUESSEL); } catch { return null; }
}

export function tokenSetzen(wert: string | null) {
  try {
    if (wert) localStorage.setItem(TOKEN_SCHLUESSEL, wert);
    else localStorage.removeItem(TOKEN_SCHLUESSEL);
  } catch { /* privater Modus: dann eben nur für diese Sitzung */ }
}

// Wird ausgelöst, wenn der Zugang während der Nutzung gesperrt wird. Die
// Anwendung hängt sich hier ein und meldet den Benutzer ab.
type Abmeldegrund = { nachricht: string; code?: FehlerCode };
let beiAbmeldung: ((grund: Abmeldegrund) => void) | null = null;
export function abmeldungBehandeln(fn: (grund: Abmeldegrund) => void) {
  beiAbmeldung = fn;
}

type Optionen = {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  daten?: unknown;
  signal?: AbortSignal;
};

export async function api<T = unknown>(pfad: string, opt: Optionen = {}): Promise<T> {
  const { methode = 'GET', daten, signal } = opt;
  const t = token();

  let antwort: Response;
  try {
    antwort = await fetch(BASIS + pfad, {
      method: methode,
      headers: {
        ...(daten !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      ...(daten !== undefined ? { body: JSON.stringify(daten) } : {}),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiFehler(0, 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen.');
  }

  const text = await antwort.text();
  let koerper: any = null;
  try { koerper = text ? JSON.parse(text) : null; } catch { koerper = text; }

  if (!antwort.ok) {
    const nachricht =
      (koerper && typeof koerper === 'object' && (koerper.error || koerper.message)) ||
      `Fehler ${antwort.status}`;
    const fehler = new ApiFehler(antwort.status, String(nachricht), koerper?.code, koerper);

    // Abgelaufenes oder ungültiges Token, oder Zugang gesperrt: abmelden.
    // Nicht bei jedem 403: „Nur Administratoren" ist ein Verbot, kein
    // Rauswurf. Früher meldete genau das Mitarbeiter ab. Ebenso ein
    // vertipptes Passwort beim Bestätigen (401 PASSWORT_FALSCH) — die Sitzung gilt weiter.
    if ((antwort.status === 401 && fehler.code !== 'PASSWORT_FALSCH') || fehler.istGesperrt || fehler.code === 'TOKEN_UNGUELTIG') {
      beiAbmeldung?.({ nachricht: String(nachricht), code: koerper?.code });
    }
    throw fehler;
  }

  // Das Backend antwortet uneinheitlich: mal { success, data }, mal direkt.
  // Hier auf eine Form bringen, damit die Oberfläche es nicht wissen muss.
  if (koerper && typeof koerper === 'object' && 'success' in koerper && 'data' in koerper) {
    return koerper.data as T;
  }
  return koerper as T;
}
